import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Business Presence";
const h1Text = "Your strategic partner for a strong local business presence in the DACH region. With a representation and address in Duesseldorf, we position your ICT company excellently, securely, and cost-effectively in the market.";
const heroImage = "https://net-basis.net/Bilder/duesseldorf-media-harbor-rhine-tower-business-location.webp";
const heroAlt = "Düsseldorf Media Harbor and Rhine Tower represent a local business presence in the DACH market.";
const heroClaimLine1 = "Business Hub";
const heroClaimLine2 = "Düsseldorf";
const benefitsTitle = "How you benefit from our __Business Presence__ expertise:";
const p1 = "For many companies, entering new markets as well as their successful development involves high expenditure. Furthermore, the sensitivities, different views or expectations of potential international business partners are regularly underrated. These partners usually prefer to have a local contact who speaks their language and is familiar with their local customs, their country's law and their trade practices. Lack of market, country and language skills, as well as insufficient personnel or financial resources thus represent considerable hurdles.";
const p2Text = "This is precisely where NetBasis International helps with its \"Business Presence\" service, acting as a link between your company and the new markets, to effectively overcome all potential difficulties or obstacles.";
const p2Parts = p2Text.split(COMPANY_NAME);
const p3 = "With a local commercial presence and address in Düsseldorf - not only upholds your company name or provides you with a local phone number and contact person, but also conducts your complete business in your name – you will be excellently and cost-effectively positioned.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Consistent minimization of costs, operational effort, time, and risk",
  "No necessity for your own legal presence or corporate entity on-site",
  "Immediate and flexible market entry opportunities",
  "Favorable chance for a well-founded market and product test",
  "No internal personnel or administrative infrastructure required",
  "More efficient follow-up with already existing business contacts",
  "Sustainable relief for your own export department",
  "Complete elimination of language barriers through direct and simple communication",
  "Immediate access to databases, address lists, and integration into networks",
  "Optimal entry or seamless transition to your own local branch office",
];

export default function BusinessPresenceEnPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Business Presence | NetBasis International"
        description="Your local branch in the DACH market without administrative effort – professional, present, and ready to act immediately."
        ogUrl="https://net-basis.net/en/services/business-development/business-presence"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[22%] md:pt-[10%] lg:pt-[12%] pl-[8%] md:pl-[10%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[trainingTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  {heroClaimLine2 && <span className="block">{heroClaimLine2}</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 text-sm md:text-base mb-4">
              {h1Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>{p3}</p>
            </div>

            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}